/**
 * SearchBar Module - Following guidelines
 * Pure Tailwind, no inline styles, array mapping
 */

class SearchBar {
	constructor() {
		// Configuration using arrays
		this.CONFIG = {
			SELECTORS: {
				toggle: '.ui-h-sb',
				bar: '.ui-h-sbar',
				close: '.ui-h-sc',
				input: '.ui-h-sbar input[type="search"]',
				form: '.ui-h-sbar form',
				mainHeader: '.ui-h-main',
				metaNav: '.ui-h-meta',
			},
			STATES: {
				bar: {
					open: ['translate-x-0', 'pointer-events-auto'],
					closed: ['translate-x-full', 'pointer-events-none'],
				},
			},
		}

		// Get elements
		this.elements = this.getElements()

		// Initialize if elements exist
		if (this.elements.toggle && this.elements.bar && this.elements.close) {
			this.init()
		}
	}

	getElements() {
		const { SELECTORS } = this.CONFIG
		return {
			toggle: document.querySelector(SELECTORS.toggle),
			bar: document.querySelector(SELECTORS.bar),
			close: document.querySelector(SELECTORS.close),
			input: document.querySelector(SELECTORS.input),
			form: document.querySelector(SELECTORS.form),
			mainHeader: document.querySelector(SELECTORS.mainHeader),
			metaNav: document.querySelector(SELECTORS.metaNav),
		}
	}

	init() {
		// Bind events
		this.bindEvents()
		this.bindGlobalEvents()

		// Initial sync
		this.syncHeight()

		// Sync again after full page load (for meta nav height)
		window.addEventListener('load', () => {
			this.syncHeight()
		})

		// Sync on resize
		window.addEventListener('resize', () => {
			this.syncHeight()
		})
	}

	bindEvents() {
		// Open search bar
		this.elements.toggle.addEventListener('click', e => {
			e.preventDefault()
			this.open()
		})

		// Close search bar
		this.elements.close.addEventListener('click', e => {
			e.preventDefault()
			this.close()
		})

		// Handle search submission
		if (this.elements.input) {
			this.elements.input.addEventListener('keydown', e => {
				if (e.key === 'Enter') {
					this.handleSubmit(e)
				}
			})
		}
	}

	bindGlobalEvents() {
		// Close on click outside
		document.addEventListener('click', e => {
			const { toggle, bar } = this.elements
			if (!toggle.contains(e.target) && !bar.contains(e.target)) {
				this.close()
			}
		})

		// Close on ESC key
		document.addEventListener('keydown', e => {
			if (e.key === 'Escape' && this.isOpen()) {
				this.close()
			}
		})

		// Sync height on scroll (listen to header changes)
		window.addEventListener(
			'scroll',
			() => {
				this.syncHeight()
			},
			{ passive: true }
		)
	}

	syncHeight() {
		// Sync height with .ui-h-main container
		if (!this.elements.bar || !this.elements.mainHeader) return

		const scrolled = window.scrollY > 100
		const heightClass = scrolled ? 'h-[109px]' : 'h-[109px]'
		const lgHeightClass = scrolled ? 'lg:h-[73px]' : 'lg:h-[73px]'

		// Remove old height classes
		this.elements.bar.classList.remove('h-14', 'h-20', 'lg:h-14', 'lg:h-30', 'h-[109px]', 'lg:h-[109px]', 'lg:h-[73px]')

		// Add new height classes
		this.elements.bar.classList.add(heightClass, lgHeightClass)

		// Calculate top offset based on meta navigation
		let topOffset = 0
		if (this.elements.metaNav) {
			const metaHeight = this.elements.metaNav.offsetHeight
			topOffset = metaHeight
		}

		// Set top position
		this.elements.bar.style.top = `${topOffset}px`
	}

	handleSubmit(e) {
		// Let the form submit naturally - don't prevent default
		// Just validate that there's a query
		const query = this.elements.input.value.trim()

		if (!query) {
			e.preventDefault()
			return
		}

		// Submit the actual form (with correct action from template)
		if (this.elements.form) {
			// Form will submit naturally to its action URL
			// No need to prevent default or create new form
		}
	}

	open() {
		const { STATES } = this.CONFIG

		// Set state attribute
		this.elements.bar?.setAttribute('open', 'true')
		this.elements.toggle?.setAttribute('aria-expanded', 'true')

		// Swap classes
		this.swapClasses(this.elements.bar, STATES.bar.closed, STATES.bar.open)
	}

	close() {
		const { STATES } = this.CONFIG

		// Remove state attribute
		this.elements.bar?.removeAttribute('open')
		this.elements.toggle?.setAttribute('aria-expanded', 'false')

		// Swap classes
		this.swapClasses(this.elements.bar, STATES.bar.open, STATES.bar.closed)

		// Clear input
		if (this.elements.input) {
			this.elements.input.value = ''
		}
	}

	isOpen() {
		return this.elements.bar?.hasAttribute('open')
	}

	/**
	 * Helper to swap class arrays cleanly
	 */
	swapClasses(element, remove, add) {
		if (!element) return
		element.classList.remove(...remove)
		element.classList.add(...add)
	}
}

// Initialize on DOM ready
if (document.readyState === 'loading') {
	document.addEventListener('DOMContentLoaded', () => new SearchBar())
} else {
	new SearchBar()
}

// Export for use
window.SearchBar = SearchBar
